import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ManufacturerToken, FilterTemplateToken } from '@sleed/bizcommerce-api';
import { manufacturersApi, filterTemplatesApi, binaryRepoImagesApi } from '~/lib/api/client';
import { formToToken, type ManufacturerFormValues } from '~/lib/schemas/manufacturer';

export const manufacturerKeys = {
	all: ['manufacturers'] as const,
	list: () => [...manufacturerKeys.all, 'list'] as const,
	detail: (id: number | string) => [...manufacturerKeys.all, 'detail', id] as const
};

export function useManufacturersList() {
	return useQuery<ManufacturerToken[]>({
		queryKey: manufacturerKeys.list(),
		queryFn: async () => (await manufacturersApi.getAll()).data.items ?? []
	});
}

/** Single manufacturer by id (full record incl. SEO/description/images). `create` → null. */
export function useManufacturer(id: string) {
	const numericId = Number(id);
	const enabled = Number.isFinite(numericId) && numericId > 0;
	return useQuery<ManufacturerToken | null>({
		queryKey: manufacturerKeys.detail(id),
		queryFn: async () => (enabled ? (await manufacturersApi.getById(numericId)).data : null),
		enabled
	});
}

/** Filter templates for the manufacturer's filter-template dropdown. */
export function useFilterTemplatesList() {
	return useQuery<FilterTemplateToken[]>({
		queryKey: ['filter-templates', 'list'],
		queryFn: async () => (await filterTemplatesApi.getAll()).data.items ?? []
	});
}

/**
 * Upload a manufacturer image into slot `key` ("1" | "2"). The manufacturer must already exist
 * (id > 0); the generated client dropped the multipart param, so we attach the File as form-data
 * keyed by the slot subkey (mirrors the banner upload + the legacy BinaryRepoImages/UploadManufacturer
 * contract).
 */
export async function uploadManufacturerImage(manufacturerId: number, key: '1' | '2', file: File): Promise<void> {
	const fd = new FormData();
	fd.append(key, file);
	await binaryRepoImagesApi.uploadManufacturer(manufacturerId, key, { data: fd });
}

export function useManufacturerMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: manufacturerKeys.all });
	const create = useMutation({
		mutationFn: async (form: ManufacturerFormValues) => (await manufacturersApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (form: ManufacturerFormValues) => (await manufacturersApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({ mutationFn: async (id: number) => (await manufacturersApi.remove(id)).data, onSuccess: invalidate });
	return { create, update, remove };
}
